import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./CoreRpcCourses.css";
import { API_BASE_URL } from "../config";
import OfferPopup from "./OfferPopup";

/* ===== MICRO CONTENT PER COURSE ===== */
const rpcCopy = {
  "Small RPC": [
    "Perfect starting point for drone enthusiasts.",
    "Covers basic flight operations and safety.",
    "DGCA-certified training with hands-on flying.",
  ],
  "Medium RPC": [
    "Designed for advanced drone operations.",
    "Focus on precision flying and airspace rules.",
    "DGCA-approved certification with practical sessions.",
  ],
  "Small And Medium RPC": [
    "Complete RPC training in one program.",
    "Covers both Small and Medium classifications.",
    "Ideal for comprehensive drone mastery.",
  ],
  "RPC Upgradation": [
    "Upgrade your existing RPC license.",
    "Advanced airspace and regulatory training.",
    "Ideal for pilots moving to higher category drones.",
  ],
};

const CoreRpcCourses = ({ onConsultClick }) => {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [popupOffer, setPopupOffer] = useState(null);
  const [activeOffer, setActiveOffer] = useState(null);
  const [showReopenButton, setShowReopenButton] = useState(false);
  const [loading, setLoading] = useState(true);

  const sectionRef = useRef(null);
  const refs = useRef([]);

  /* ================= FETCH COURSES ================= */
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/courses`)
      .then((res) => res.json())
      .then((data) => {
        setCourses(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  /* ================= FADE ANIMATION ================= */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.isIntersecting
            ? entry.target.classList.add("fade-show")
            : entry.target.classList.remove("fade-show");
        });
      },
      { threshold: 0.2 },
    );

    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [courses]);

  /* ================= FETCH ACTIVE OFFERS ================= */
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/offers/active`)
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data) || !data.length) return;

        setOffers(data);

        const popup = data.find((o) => o.showPopup && o.isActive);
        if (popup) setActiveOffer(popup);
      })
      .catch(() => { });
  }, []);

  /* ================= POPUP LOGIC ================= */
  useEffect(() => {
    if (!activeOffer || !sectionRef.current) return;

    const popupShown = sessionStorage.getItem("rpc_offer_popup_shown");
    if (popupShown === "true") return;

    const showPopup = () => {
      setPopupOffer(activeOffer);
      sessionStorage.setItem("rpc_offer_popup_shown", "true");
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          showPopup();
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(sectionRef.current);

    /* MOBILE SAFETY TRIGGER */
    const mobileCheck = setTimeout(() => {
      const rect = sectionRef.current.getBoundingClientRect();

      if (rect.top < window.innerHeight) {
        showPopup();
        observer.disconnect();
      }
    }, 800);

    return () => {
      observer.disconnect();
      clearTimeout(mobileCheck);
    };
  }, [activeOffer]);

  /* ================= OFFER MATCHER ================= */
  const getOfferForCourse = (courseId) => {
    return offers.find((offer) => {
      if (offer.applyToAll) return true;
      if (Array.isArray(offer.courses)) {
        return offer.courses.includes(courseId);
      }
      return false;
    });
  };

  /* ================= POPUP CLOSE ================= */
  const handlePopupClose = () => {
    setPopupOffer(null);
    setShowReopenButton(true);
  };

  if (loading) {
    return <div className="rpc-loading">Loading courses…</div>;
  }

  return (
    <section className="rpc-section" ref={sectionRef}>
      {/* HEADER */}
      <header
        className="rpc-header fade-item"
        ref={(el) => (refs.current[0] = el)}
      >
        <h1>DGCA Approved RPC Courses</h1>
        <p>
          Learn to fly drones professionally with structured training and
          official Remote Pilot Certification.
        </p>
      </header>

      {/* COURSE CARDS */}
      <div className="rpc-card-grid">
        {courses.map((course, i) => {
          const offer = getOfferForCourse(course._id);
          const content = rpcCopy[course.name] || rpcCopy["Small RPC"];

          return (
            <div
              key={course._id}
              className="rpc-card fade-item"
              ref={(el) => (refs.current[i + 1] = el)}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="rpc-image-wrapper">
                {offer?.showBadge && (
                  <span className="rpc-offer-badge">
                    🔥 {offer.badgeText || "SPECIAL OFFER"}
                  </span>
                )}

                <img
                  src={
                    course.image
                      ? `${API_BASE_URL}${course.image}`
                      : "/service1.webp"
                  }
                  alt={course.name}
                />

                <span className="rpc-badge">DGCA Approved</span>
              </div>

              <div className="rpc-card-content">
                <h2>{course.name}</h2>

                <div className="rpc-copy">
                  {content.map((line, idx) => (
                    <p key={idx}>{line}</p>
                  ))}
                </div>

                <div className="rpc-meta">
                  <span>
                    <b>Duration - </b>
                    {course.days || "5–8"} Days
                  </span>

                  <span className="rpc-price">
                    ₹{course.price?.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              <div className="rpc-actions">
                <button
                  className="rpc-primary-btn"
                  onClick={() =>
                    navigate(`/course-enroll-template/${course._id}`)
                  }
                >
                  Enroll Now
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="core-rpc-cta">
        <h3>Not sure which course fits you?</h3>
        <p>Talk to our trainers and get clear guidance.</p>
        <button onClick={() => onConsultClick && onConsultClick()}>
          Book Free Consultation →
        </button>
      </div>

      {/* POPUP */}
      {popupOffer && (
        <OfferPopup offer={popupOffer} onClose={handlePopupClose} />
      )}

      {/* REOPEN BUTTON */}
      {showReopenButton && activeOffer && (
        <div
          className="offer-chip-floating"
          onClick={() => {
            setPopupOffer(activeOffer);
            setShowReopenButton(false);
          }}
        >
          <span className="offer-chip-icon">🔥</span>
          <span className="offer-chip-text">Limited Offer</span>
        </div>
      )}
    </section>
  );
};

export default CoreRpcCourses;
